import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { TEAM_ADMIN_MANAGED_FEATURE_FLAG_ENVIRONMENT_COMPARISON } from '../../../services/managed-feature-flags/managed-feature-flag-keys';
import {
  ManagedFeatureFlagEnvironmentComparison,
  ManagedFeatureFlagEnvironmentComparisonResult,
  ManagedFeatureFlagRule,
  ManagedFeatureFlagSnapshot,
  ManagedFeatureFlagsService,
} from '../../../services/managed-feature-flags/managed-feature-flags.service';

type ComparisonState = 'loading' | 'success' | 'unsupported' | 'unavailable' | 'authorization' | 'error';

@Component({
    selector: 'app-managed-feature-flags-comparison',
    templateUrl: './managed-feature-flags-comparison.component.html',
    styleUrls: ['./managed-feature-flags-comparison.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class ManagedFeatureFlagsComparisonComponent implements OnDestroy, OnInit {
  protected comparison: ManagedFeatureFlagEnvironmentComparison | null = null;
  protected state: ComparisonState = 'loading';
  protected unavailableEnvironments: Array<'qa' | 'production'> = [];
  protected expandedKeys = new Set<string>();
  private requestGeneration = 0;
  private destroyed = false;

  constructor(
    private readonly managedFeatureFlagsService: ManagedFeatureFlagsService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
  ) {}

  async ngOnInit(): Promise<void> {
    await this.loadComparison();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    this.requestGeneration += 1;
  }

  protected async loadComparison(): Promise<void> {
    if (this.destroyed) {
      return;
    }

    const requestGeneration = ++this.requestGeneration;
    this.comparison = null;
    this.unavailableEnvironments = [];
    this.expandedKeys.clear();
    this.state = 'loading';
    let rolloutFlagEvaluated = false;

    try {
      const comparisonEnabled = await this.managedFeatureFlagsService.isEnabled(
        TEAM_ADMIN_MANAGED_FEATURE_FLAG_ENVIRONMENT_COMPARISON,
        'team-admin',
      );
      rolloutFlagEvaluated = true;
      if (!this.isActiveRequest(requestGeneration)) {
        return;
      }
      if (!comparisonEnabled) {
        this.returnToFlags();
        return;
      }

      const comparison = await this.managedFeatureFlagsService.getEnvironmentComparison();
      if (!this.isActiveRequest(requestGeneration)) {
        return;
      }

      this.comparison = comparison;
      this.state = 'success';
    } catch (error: any) {
      if (!this.isActiveRequest(requestGeneration)) {
        return;
      }

      if (!rolloutFlagEvaluated) {
        this.returnToFlags();
        return;
      }

      this.comparison = null;
      const status = error?.status;
      const code = error?.data?.error || error?.error;
      if (status === 409 || code === 'MANAGED_FEATURE_FLAG_COMPARISON_UNSUPPORTED_ENVIRONMENT') {
        this.state = 'unsupported';
      } else if (status === 502 || code === 'MANAGED_FEATURE_FLAG_ENVIRONMENT_UNAVAILABLE') {
        this.state = 'unavailable';
        this.unavailableEnvironments = Array.isArray(error?.data?.unavailableEnvironments)
          ? error.data.unavailableEnvironments.filter((environment: unknown) =>
            environment === 'qa' || environment === 'production')
          : [];
      } else if (status === 401
        || status === 403
        || (status === 400 && error?.data?.error === 'Only admin can do this action')) {
        this.state = 'authorization';
      } else {
        this.state = 'error';
      }
    }
  }

  private isActiveRequest(requestGeneration: number): boolean {
    return !this.destroyed && requestGeneration === this.requestGeneration;
  }

  protected backToFlags(): void {
    this.returnToFlags();
  }

  private returnToFlags(): void {
    void this.router.navigate(['/superadmin/managed-feature-flags'], {
      queryParamsHandling: 'preserve',
    });
  }

  protected toggleExpanded(result: ManagedFeatureFlagEnvironmentComparisonResult): void {
    if (this.expandedKeys.has(result.key)) {
      this.expandedKeys.delete(result.key);
    } else {
      this.expandedKeys.add(result.key);
    }
  }

  protected isExpanded(result: ManagedFeatureFlagEnvironmentComparisonResult): boolean {
    return this.expandedKeys.has(result.key);
  }

  protected statusLabel(status: ManagedFeatureFlagEnvironmentComparisonResult['status']): string {
    return {
      same: 'Same',
      changed: 'Changed',
      'only-in-qa': 'Only in QA',
      'only-in-production': 'Only in Production',
    }[status];
  }

  protected fieldLabel(field: string): string {
    return {
      presence: 'Presence',
      name: 'Name',
      description: 'Description',
      appTargets: 'App targets',
      isActive: 'Active status',
      defaultEnabled: 'Default state',
      userOverrides: 'User overrides',
      rules: 'Targeting rules',
      schemaVersion: 'Schema version',
    }[field] || field;
  }

  protected differentFieldLabels(result: ManagedFeatureFlagEnvironmentComparisonResult): string {
    return result.differentFields.map((field) => this.fieldLabel(field)).join(', ');
  }

  protected unavailableEnvironmentLabels(): string {
    return this.unavailableEnvironments.map((environment) => this.environmentLabel(environment)).join(', ');
  }

  protected environmentLabel(environment: string): string {
    return environment === 'qa' ? 'QA' : environment === 'production' ? 'Production' : environment;
  }

  protected appTargets(snapshot: ManagedFeatureFlagSnapshot | null): string {
    if (!snapshot) { return 'Not present'; }
    return snapshot.appTargets.length
      ? snapshot.appTargets.map((app) => app === 'backend' ? 'Backend' : app === 'team-admin' ? 'Team Admin' : 'Customer App').join(', ')
      : 'None';
  }

  protected booleanValue(value: boolean | null | undefined): string {
    return value == null ? 'Not present' : value ? 'Yes' : 'No';
  }

  protected textValue(value: string | null | undefined, snapshot: ManagedFeatureFlagSnapshot | null): string {
    if (!snapshot) { return 'Not present'; }
    return value ? value : 'None';
  }

  protected values(values: string[] | undefined, snapshot: ManagedFeatureFlagSnapshot | null): string {
    if (!snapshot) { return 'Not present'; }
    return values && values.length ? values.join(', ') : 'None';
  }

  protected rules(snapshot: ManagedFeatureFlagSnapshot | null): Array<Omit<ManagedFeatureFlagRule, 'id'>> {
    return snapshot?.rules || [];
  }

  protected hasSnapshot(snapshot: ManagedFeatureFlagSnapshot | null): boolean {
    return !!snapshot;
  }

  protected trackByKey(_: number, result: ManagedFeatureFlagEnvironmentComparisonResult): string {
    return result.key;
  }
}
